/**
 * The vehicle, second edition: what car it is, four figures, the fill-ups and
 * the service book each with its form in place, where the money goes, and what
 * is coming up -- worked out from the entries, not typed in as reminders.
 *
 * Written once for both layouts (`compact` is the phone).
 */

import { h } from '../vdom.js';
import { localDay, numberText,
  parseNumber, toBase, CAR_EXPENSE_TYPES, kmToMiles, milesToKm, litresToGallons, gallonsToLitres
} from '../../../shared/model.js';
import { C, MONO, css, tap, input, select, emptyLine, currencyOptions, carTypeLabel, datePicker, saveFile } from './common.js';
import { buildIcs } from '../ics.js';
import { pill } from './cashflow.js';
import { DANGER_TEXT, GAIN_BG, GAIN_LINE, GAIN_LABEL } from './whatif.js';

/** One colour per kind of cost, fuel included, the same on the split, the filters and the rows. */
export const VEHICLE_COLORS = {
  Fuel: '#3DE8B0',
  Maintenance: '#7AA2F7',
  Repair: '#F2A0B8',
  Insurance: '#5FD0C5',
  Registration: '#B48EF0',
  Tyres: '#E3B86B',
  'Loan payment': '#D98A6B',
  Other: '#8FA3BA'
};
const colorOf = type => VEHICLE_COLORS[type] || VEHICLE_COLORS.Other;
/* An oil change is recognised by what was written about it. */
const OIL = /olej|oil/i;
const INTERVALS = { metric: [10000, 15000, 20000, 30000], imperial: [5000, 7500, 10000, 15000] };

const pct = ratio => (Math.max(0, Math.min(1, ratio)) * 100).toFixed(2) + '%';
const box = (compact, style, ...children) => h('div', {
  style: css({
    padding: compact ? '14px' : '16px 18px 18px', border: `1px solid ${C.stroke}`, borderRadius: compact ? '16px' : '14px',
    background: C.card, display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, ...style
  })
}, ...children);
const head = (compact, title, sub) => h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
  h('div', { style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700 }) }, title),
  sub && h('div', { key: 's', style: css({ fontSize: '12.5px', color: C.muted }) }, sub));
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);
const today = () => localDay();
const daysBetween = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 864e5);
const yearLater = date => (Number(date.slice(0, 4)) + 1) + date.slice(4);

/** A tick box with its label, as large as a finger. */
const checkBox = (on, text, onToggle, key) => h('div', {
  key, ...tap(onToggle, text), role: 'checkbox', 'aria-checked': on ? 'true' : 'false',
  style: css({
    alignSelf: 'flex-start', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px 0 10px', borderRadius: '10px', cursor: 'pointer',
    border: `1px solid ${on ? C.accent : C.strokeHi}`, background: on ? GAIN_BG : 'transparent'
  })
},
  h('span', {
    style: css({
      flex: '0 0 auto', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px',
      border: `1px solid ${on ? C.accent : C.strokeHi}`, background: on ? C.accent : 'transparent', color: C.bg, fontSize: '13px', fontWeight: 700
    })
  }, on ? '✓' : ''),
  h('span', { style: css({ fontSize: '13px', lineHeight: 1.35 }) }, text));

const iconButton = (glyph, onClick, label, danger = false) => h('div', {
  key: label, ...tap(onClick, label), class: danger ? 'pt-remove' : 'pt-button', title: label,
  style: css({
    width: '36px', height: '36px', flex: '0 0 36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
    border: danger ? 'none' : `1px solid ${C.stroke}`, color: danger ? C.faint : C.muted, fontSize: danger ? '16px' : '12px', cursor: 'pointer'
  })
}, glyph);
const openButton = (label, sub, onClick, key) => h('div', {
  key, ...tap(onClick, label), class: 'pt-button',
  style: css({
    minHeight: '56px', padding: '10px 16px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px', display: 'flex', flexWrap: 'wrap',
    alignItems: 'center', justifyContent: 'center', gap: '4px 10px', textAlign: 'center', cursor: 'pointer'
  })
}, mono(label, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
  h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, sub));
const formShell = ({ key, id, editing, title, onClose, closeLabel }, ...children) => h('div', {
  key, id, class: 'pt-rise',
  style: css({
    padding: '14px', borderRadius: '12px', background: C.panel, border: `1px solid ${editing ? C.goldDeep : C.stroke}`,
    display: 'flex', flexDirection: 'column', gap: '12px'
  })
},
  h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
    mono(title, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
    h('div', { style: css({ flex: 1 }) }),
    iconButton('×', onClose, closeLabel)),
  ...children);
const label = text => h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, text);
const fieldBox = (inputs, name, text, value, { unit = null, placeholder = '0', number = true, onEnter, wide = false } = {}) => h('label', {
  key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, ...(wide ? { gridColumn: '1 / -1' } : {}) })
},
  label(text),
  h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '6px', height: '44px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel, padding: '0 12px' }) },
    input(inputs, name, placeholder, {
      type: number ? 'number' : 'text', value, onEnter,
      style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: number ? MONO : 'inherit', fontSize: number ? '14px' : '13.5px' }
    }),
    unit && mono(unit, { key: 'u', fontSize: '11.5px', color: C.faint })));
const pickerField = (text, picker) => h('div', { key: 'date', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) }, label(text), picker);
const triggerStyle = {
  height: '44px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '10px',
  border: `1px solid ${C.strokeHi}`, background: C.panel, fontSize: '13px', color: C.text, letterSpacing: '.6px'
};
/* Offered when adding only: a correction keeps the currency the entry was made in, as the API does. */
const currencyField = (inputs, state, t, name, current) => h('label', { key: 'cur', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
  label(t.shell.ivCurF),
  select(inputs, name, currencyOptions(state, current), { height: '44px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px' }, { value: current }));

/**
 * The figures the cards share: costs in the base currency, the distance each
 * full tank was measured over, and this year's spending.
 */
export function vehicleFacts(metrics, state) {
  const base = metrics.baseCurrency;
  const rates = metrics.rates || {};
  const inBase = (value, currency) => toBase(Number(value) || 0, currency || base, base, rates);
  const fills = metrics.fuelRows || [];
  /* fuelRows run newest first: each full tank was measured from the next full one below it. */
  const spans = {};
  let measured = 0;
  fills.forEach((row, i) => {
    if (!row.full || row.economy == null) return;
    const older = fills.slice(i + 1).find(other => other.full);
    if (older) { spans[row.id] = row.odo - older.odo; measured += row.odo - older.odo; }
  });
  const year = String(state.month || today()).slice(0, 4);
  const fuelYear = fills.filter(row => String(row.date).startsWith(year)).reduce((sum, row) => sum + (inBase(row.cost, row.currency) || 0), 0);
  const carYear = (metrics.carRows || []).filter(row => String(row.date).startsWith(year)).reduce((sum, row) => sum + (inBase(row.amount, row.currency) || 0), 0);
  return { inBase, spans, measured, year, fuelYear, spentYear: fuelYear + carYear };
}

// ---------------------------------------------------------------- strip ---

export function vehicleStrip({ metrics, t, fmt, actions, state, compact = false }) {
  const s = t.shell;
  const car = metrics.vehicle;
  const edit = h('div', { key: 'edit' }, iconButton('✎', () => actions.setSheet(state.sheet === 'vehicle' ? null : 'vehicle'), s.editVehicle));
  const spec = (text, value) => h('div', { key: text, style: css({ display: 'flex', flexDirection: 'column', gap: '3px' }) },
    h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, text),
    mono(value, { fontSize: '13.5px', fontWeight: 700, whiteSpace: 'nowrap' }));
  return h('div', {
    style: css({
      padding: compact ? '12px 14px' : '14px 18px', border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.card,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 28px'
    })
  },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 160px', minWidth: 0 }) },
      mono(s.vhCar, { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
      h('div', { style: css({ fontSize: '17px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }) }, car.name || s.vhNoName)),
    /* Beside the name on a phone, where the figures take a line of their own below. */
    compact && edit,
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '10px 28px', flex: compact ? '1 1 100%' : '0 1 auto' }) },
      spec(s.vhOdo, fmt.distance(car.odo)),
      car.startOdo > 0 && spec(s.vhSince, '+' + fmt.number(car.distance, 0) + ' ' + fmt.units.distance),
      car.tank != null && spec(s.vhTank, fmt.number(Math.round(car.tank * 10) / 10) + ' ' + fmt.units.volume),
      car.range != null && spec(s.vhRange, '~' + fmt.number(car.range, 0) + ' ' + fmt.units.distance)),
    !compact && edit
  );
}

// -------------------------------------------------------------- figures ---

export function vehicleKpis({ metrics, t, fmt, state, compact = false, facts }) {
  const s = t.shell;
  const car = metrics.vehicle;
  const f = facts || vehicleFacts(metrics, state);
  const tile = (text, value, sub, { tinted = false, color = C.text, unit = null } = {}) => h('div', {
    style: css({
      padding: compact ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0,
      border: `1px solid ${tinted ? GAIN_LINE : C.stroke}`, background: tinted ? GAIN_BG : C.card,
      display: 'flex', flexDirection: 'column', gap: compact ? '5px' : '7px'
    })
  },
    mono(text, { fontSize: compact ? '10px' : '10.5px', letterSpacing: '1.2px', color: tinted ? GAIN_LABEL : C.muted }),
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '6px', minWidth: 0, whiteSpace: 'nowrap' }) },
      mono(value, { fontSize: compact ? '18px' : '24px', fontWeight: 700, letterSpacing: compact ? 0 : '-0.6px', color }),
      /* The unit after the figure, smaller: "8,72 l/100 km" at full size does not fit a phone's half-width tile. */
      unit && mono(unit, { key: 'u', fontSize: compact ? '11px' : '13px', color: C.muted })),
    h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: C.textDim, lineHeight: 1.4 }) }, sub));

  return h('div', { style: css({ display: 'grid', gap: '12px', gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))' }) },
    /* Nothing logged yet: no "0.00/km over 1 km" -- the 1 only guards a division. */
    !(facts.fuelSpent > 0 || (metrics.carRows || []).length || (metrics.fuelRows || []).length)
      ? tile(t.fx.costPerUnit(fmt.units.distance), '—', s.economyNoFills)
      /* Costs logged, but the odometer has not moved past its first reading. */
      : car.costPerDistance == null
      ? tile(t.fx.costPerUnit(fmt.units.distance), '—', s.vhPerKmNoDistance)
      : tile(t.fx.costPerUnit(fmt.units.distance), fmt.perDistance(car.costPerDistance),
        s.vhPerKmSub(fmt.number(car.distance, 0) + ' ' + fmt.units.distance), { tinted: true, color: C.accent }),
    tile(t.avgEconomy, car.avgEconomy == null ? '—' : fmt.number(car.avgEconomy, fmt.units.economyDp),
      car.avgEconomy != null
        ? s.vhConsSub(fmt.number(f.measured, 0) + ' ' + fmt.units.distance)
        : (car.fills > 0 ? s.economyNeedsTwo : s.economyNoFills),
      { unit: car.avgEconomy == null ? null : fmt.units.economy }),
    tile(s.vhYear(f.year), fmt.money(f.spentYear), s.vhYearSub(fmt.money(f.fuelYear))),
    tile(s.ownershipTotal, fmt.money(car.totalOwnership),
      car.purchasePrice > 0 ? s.vhOwnSub(fmt.money(car.purchasePrice), fmt.money(car.total)) : s.vhNoPrice)
  );
}

// ---------------------------------------------------------------- fuel ---

export function fuelCard({ metrics, t, fmt, state, inputs, actions, compact = false, facts }) {
  const s = t.shell;
  const ui = state.vehUi;
  const car = metrics.vehicle;
  const f = facts || vehicleFacts(metrics, state);
  const rows = metrics.fuelRows || [];
  const total = rows.reduce((sum, row) => sum + (f.inBase(row.cost, row.currency) || 0), 0);

  const startEdit = row => {
    actions.setVehUi({ fuelOpen: true, fuelEdit: row.id, full: row.full !== false, fuelDate: row.date, rev: ui.rev + 1 });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('fuel-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const line = row => {
    const km = f.spans[row.id];
    const perUnit = row.volume > 0 ? row.cost / row.volume : null;
    return h('div', {
      key: row.id,
      style: css({
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 18px', padding: '12px 4px',
        borderBottom: `1px solid ${C.line}`, background: ui.fuelEdit === row.id ? C.line : 'transparent'
      })
    },
      h('div', { style: css({ flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
          mono(fmt.day(row.date), { fontSize: '13px', fontWeight: 700 }),
          mono(row.full ? s.vhFull : s.fuelPartialShort.toUpperCase(), {
            padding: '1px 6px', borderRadius: '5px', background: row.full ? GAIN_BG : C.stroke,
            fontSize: '10px', fontWeight: 700, letterSpacing: '.4px', color: row.full ? C.accentHi : C.muted
          })),
        mono(fmt.distance(row.odo), { fontSize: '11.5px', color: C.muted })),
      h('div', { style: css({ flex: '0 0 120px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }) },
        mono(fmt.money(row.cost, 2, row.currency), { fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }),
        mono(fmt.number(row.volume, 1) + ' ' + fmt.units.volume + (perUnit ? ' · ' + fmt.money(perUnit, 2, row.currency) + '/' + fmt.units.volume : ''),
          { fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' })),
      h('div', { style: css({ flex: '0 0 112px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }) },
        mono(row.economy == null ? '—' : fmt.number(row.economy, fmt.units.economyDp), {
          fontSize: '14px', fontWeight: 700, color: row.economy == null ? C.faint : (row.aboveAverage ? C.accent : DANGER_TEXT)
        }),
        h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) },
          row.economy != null
            ? fmt.units.economy + (km ? ' · ' + fmt.number(km, 0) + ' ' + fmt.units.distance : '')
            : (row.full ? s.vhFirstFull : s.vhWaitsFull))),
      h('div', { style: css({ flex: '0 0 auto', display: 'flex', gap: '4px', marginLeft: compact ? 'auto' : 0 }) },
        iconButton('✎', () => startEdit(row), s.editFuelTitle),
        iconButton('×', () => actions.removeFuel(row.id), s.removeLabel, true))
    );
  };

  return box(compact, { gap: '6px' },
    h('div', { style: css({ paddingBottom: '8px' }) },
      head(compact, t.fuelLog, rows.length ? s.vhFuelSub(rows.length, fmt.money(total)) : null)),
    rows.length
      ? h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) }, ...rows.map(line))
      : h('div', { key: 'empty' }, emptyLine(s.economyNoFills)),
    h('div', { key: 'add', style: css({ marginTop: '12px' }) },
      ui.fuelOpen
        ? fuelForm({ metrics, t, fmt, state, inputs, actions, compact })
        : openButton(s.vhFuelNew, s.vhFuelNewHint, () => actions.setVehUi({ fuelOpen: true, fuelEdit: null, full: true, fuelDate: today(), rev: ui.rev + 1 }), 'open'))
  );
}

/** A new fill-up, or the one whose ✎ was pressed -- in the reader's units both ways, like the dialog it replaces. */
function fuelForm({ metrics, t, fmt, state, inputs, actions, compact }) {
  const s = t.shell;
  const ui = state.vehUi;
  const car = metrics.vehicle;
  const row = ui.fuelEdit ? (state.data?.fuel || []).find(item => item.id === ui.fuelEdit) : null;
  const imperial = fmt.units.system === 'imperial';
  const read = name => parseNumber(inputs.value(name));
  const close = () => actions.setVehUi({ fuelOpen: false, fuelEdit: null, rev: ui.rev + 1 });
  const odoText = row ? numberText(Math.round((imperial ? kmToMiles(row.odo) : row.odo) * 10) / 10) : undefined;
  const volumeText = row ? numberText(Math.round((imperial ? litresToGallons(row.gal) : row.gal) * 100) / 100) : undefined;
  const kept = (name, text) => inputs.value(name).trim() === String(text);
  const currency = row ? row.currency : actions.entryCurrency();

  const save = () => {
    const odo = read('vfOdo');
    const volume = read('vfVol');
    const cost = read('vfCost');
    if (odo === null || volume === null || odo < 0 || volume <= 0) return actions.showError(s.fuelNeeded);
    if (cost !== null && cost < 0) return actions.showError(s.numberNeeded);
    if (car.tank && volume > car.tank * 1.15) {
      return actions.showError(s.fillTooBig(fmt.number(Math.round(car.tank * 10) / 10) + ' ' + fmt.units.volume));
    }
    const date = ui.fuelDate || today();
    const cur = inputs.value('vfCur') || currency;
    if (row) {
      actions.saveEdit('fuel', row.id, {
        odo: kept('vfOdo', odoText) ? row.odo : (imperial ? milesToKm(odo) : odo),
        gal: kept('vfVol', volumeText) ? row.gal : (imperial ? gallonsToLitres(volume) : volume),
        cost: cost === null ? 0 : cost,
        full: ui.full,
        date
      });
    } else {
      actions.addFuel({ odo, gal: volume, cost: cost || 0, currency: cur, date, full: ui.full });
    }
    inputs.clear(['vfOdo', 'vfVol', 'vfCost']);
    close();
  };

  const grid = (...children) => h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 130 : 140}px, 1fr))`, gap: '10px' }) }, ...children);
  return formShell({
    key: 'fuelform-' + (row ? row.id : 'new') + '-' + ui.rev, id: 'fuel-form', editing: Boolean(row),
    title: row ? s.vhFuelEdit : s.vhFuelForm, onClose: close, closeLabel: s.cfClose
  },
    grid(
      pickerField(s.dateLabel, datePicker({
        id: 'veh-fuel-date', value: ui.fuelDate || today(), onPick: stamp => stamp && actions.setVehUi({ fuelDate: stamp }),
        label: s.dateLabel, title: s.serviceDateTitle, note: s.serviceDateNote, mobile: compact, state, actions, t, fmt, triggerStyle
      })),
      fieldBox(inputs, 'vfOdo', s.vhOdoF, odoText, { unit: fmt.units.distance, placeholder: String(Math.round(car.odo || 0)), onEnter: save }),
      fieldBox(inputs, 'vfVol', s.vhVolF, volumeText, { unit: fmt.units.volume, placeholder: numberText('0.0'), onEnter: save }),
      fieldBox(inputs, 'vfCost', s.vhCostF, row ? numberText(row.cost || 0) : undefined, { placeholder: numberText('0.00'), onEnter: save }),
      !row && currencyField(inputs, state, t, 'vfCur', currency)),
    h('div', {
      ...tap(() => actions.setVehUi({ full: !ui.full }), s.vhFullToggle), role: 'checkbox', 'aria-checked': ui.full ? 'true' : 'false',
      style: css({
        alignSelf: 'flex-start', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px 0 10px', borderRadius: '10px', cursor: 'pointer',
        border: `1px solid ${ui.full ? C.accent : C.strokeHi}`, background: ui.full ? GAIN_BG : 'transparent'
      })
    },
      h('span', {
        style: css({
          width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px',
          border: `1px solid ${ui.full ? C.accent : C.strokeHi}`, background: ui.full ? C.accent : 'transparent', color: C.bg, fontSize: '13px', fontWeight: 700
        })
      }, ui.full ? '✓' : ''),
      h('span', { style: css({ fontSize: '13px' }) }, s.vhFullToggle)),
    h('div', { style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }) },
      h('div', { style: css({ flex: '1 1 260px', fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, s.vhFullNote),
      h('div', { ...tap(close, s.cfCancel), style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 14px', color: C.muted, fontSize: '13px', cursor: 'pointer' }) }, s.cfCancel),
      pill(row ? s.cfSaveChanges : s.vhAdd, save, { primary: true, key: 'save' }))
  );
}

// --------------------------------------------------------------- split ---

export function vehicleSplit({ metrics, t, fmt, state, compact = false, facts }) {
  const s = t.shell;
  const f = facts || vehicleFacts(metrics, state);
  const sums = new Map();
  const add = (key, value) => sums.set(key, (sums.get(key) || 0) + (value || 0));
  (metrics.fuelRows || []).forEach(row => add('Fuel', f.inBase(row.cost, row.currency)));
  (metrics.carRows || []).forEach(row => add(row.type, f.inBase(row.amount, row.currency)));
  const parts = [...sums.entries()].filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
  const total = parts.reduce((sum, [, value]) => sum + value, 0);
  if (!total) return null;
  const name = key => (key === 'Fuel' ? s.vhFuel : carTypeLabel(t, key));
  return box(compact, { gap: '14px' },
    head(compact, s.vhSplit, s.vhSplitSub),
    h('div', { style: css({ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', gap: '2px', background: C.stroke }) },
      ...parts.map(([key, value]) => h('div', { key, title: name(key) + ' ' + fmt.percent(value / total, 0), style: css({ width: pct(value / total), height: '100%', background: colorOf(key) }) }))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column' }) },
      ...parts.map(([key, value]) => h('div', {
        key, style: css({ display: 'grid', gridTemplateColumns: '12px minmax(0,1fr) auto 48px', gap: '12px', alignItems: 'center', minHeight: '40px', borderBottom: `1px solid ${C.line}` })
      },
        h('span', { style: css({ width: '10px', height: '10px', borderRadius: '3px', background: colorOf(key) }) }),
        h('span', { style: css({ fontSize: '13.5px' }) }, name(key)),
        mono(fmt.money(value), { fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }),
        mono(fmt.percent(value / total, 0), { textAlign: 'right', fontSize: '12px', color: C.muted }))))
  );
}

// ------------------------------------------------------------ upcoming ---

/**
 * The dated reminders as calendar events, a fortnight's warning each. The oil
 * change has no date -- it comes due by distance -- and a date already past is
 * not a reminder any more.
 */
export function vehicleCalendarEvents(items, { t, name = '', from = localDay() }) {
  const s = t.shell;
  return items
    .filter(item => item.ics && item.ics.date >= from)
    .map(item => ({
      uid: item.ics.uid, date: item.ics.date,
      summary: (name ? name + ': ' : '') + item.title,
      description: s.icsFrom,
      alarm: '-P14D'
    }));
}

/**
 * What is coming up, from what is already written down: a policy renews a
 * year after the last one was paid, and oil is due a set distance after the
 * last change -- measured from the odometer at the fill nearest before it.
 */
export function vehicleUpcoming({ metrics, t, fmt, state, actions, compact = false }) {
  const s = t.shell;
  const car = metrics.vehicle;
  const now = today();
  const carRows = metrics.carRows || [];
  const items = [];

  const policy = carRows.filter(row => row.type === 'Insurance').sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  if (policy) {
    const due = yearLater(policy.date);
    const left = daysBetween(now, due);
    items.push({
      key: 'policy', title: s.vhPolicy,
      sub: s.vhLastTime(fmt.day(policy.date), fmt.amount(policy.amount, policy.currency)) + (policy.note ? ' · ' + policy.note : ''),
      when: fmt.day(due), left: left < 0 ? s.vhOverdueDays(-left) : s.vhInDays(left),
      color: left < 0 ? DANGER_TEXT : left < 30 ? C.gold : C.textDim,
      ics: { uid: 'vehicle-policy', date: due }
    });
  }

  /* The roadworthiness test: due again a year after the last one. */
  const check = carRows.filter(row => row.type === 'Inspection').sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  if (check) {
    const due = yearLater(check.date);
    const left = daysBetween(now, due);
    items.push({
      key: 'inspection', title: s.vhInspection,
      sub: s.vhLastTime(fmt.day(check.date), fmt.amount(check.amount, check.currency)) + (check.note ? ' · ' + check.note : ''),
      when: fmt.day(due), left: left < 0 ? s.vhOverdueDays(-left) : s.vhInDays(left),
      color: left < 0 ? DANGER_TEXT : left < 30 ? C.gold : C.textDim,
      ics: { uid: 'vehicle-inspection', date: due }
    });
  }

  /*
   * The change counted from: the newest entry marked as one. Entries from
   * before the mark existed are recognised by their note instead -- but only
   * while nothing is marked, so one marked change settles it for good.
   */
  const newest = rows => rows.sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  const marked = carRows.filter(row => row.oilChange);
  const oil = marked.length ? newest(marked) : newest(carRows.filter(row => OIL.test(row.note || '')));
  const system = fmt.units.system === 'imperial' ? 'imperial' : 'metric';
  const interval = car.serviceInterval || INTERVALS[system][1];
  if (oil) {
    /*
     * The odometer at the change: the reading written on the service entry.
     * Without one, the fill nearest before it stands in -- and with none
     * before, the first one after, a little late, so the date says "about".
     */
    const fills = metrics.fuelRows || [];
    const written = oil.odo > 0 ? oil.odo : null;
    const before = written ? null : fills.filter(row => row.date <= oil.date).sort((a, b) => b.odo - a.odo)[0];
    const after = written || before ? null : fills.filter(row => row.date > oil.date).sort((a, b) => a.odo - b.odo)[0];
    const at = before || after;
    const from = written ?? (at ? at.odo : null);
    const next = from === null ? null : from + interval;
    /* Any reading written on a service can be newer than the last fill-up. */
    const reading = Math.max(car.odo || 0, ...carRows.map(row => row.odo || 0));
    const left = next === null ? null : next - reading;
    const about = after ? '~' : '';
    items.push({
      key: 'oil', title: s.vhOil,
      sub: s.vhOilSub(fmt.number(interval, 0) + ' ' + fmt.units.distance, fmt.day(oil.date))
        + (written ? s.vhOilAt(fmt.distance(written)) : at ? s.vhOilEstimated : ''),
      when: next === null ? '—' : about + fmt.number(next, 0) + ' ' + fmt.units.distance,
      left: left === null ? s.vhOilNoOdo : left < 0 ? s.vhOverdueDist(fmt.number(-left, 0) + ' ' + fmt.units.distance) : s.vhInDist(fmt.number(left, 0) + ' ' + fmt.units.distance),
      color: left === null ? C.muted : left < 0 ? DANGER_TEXT : left < (system === 'imperial' ? 1000 : 1500) ? C.gold : C.textDim,
      options: INTERVALS[system]
    });
  }

  const dated = vehicleCalendarEvents(items, { t, name: car.name, from: now });
  return box(compact, { gap: '10px' },
    h('div', { key: 'head', style: css({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }) },
      head(compact, s.vhUpcoming, s.vhUpcomingSub),
      dated.length > 0 && pill(s.icsButton, () => saveFile(s.vhIcsFile, buildIcs(dated, { name: s.vhIcsCalendar }), 'text/calendar'), { key: 'ics', title: s.vhIcsTitle })),
    items.length === 0
      ? h('div', { key: 'empty', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.vhUpcomingEmpty)
      : items.map(item => h('div', {
        key: item.key,
        style: css({
          display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '4px 14px', alignItems: 'center',
          padding: '12px 14px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`
        })
      },
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '13.5px', fontWeight: 600 }) }, item.title),
          h('span', { style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.4 }) }, item.sub)),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }) },
          mono(item.when, { fontSize: '13px', fontWeight: 700, color: item.color, whiteSpace: 'nowrap' }),
          mono(item.left, { fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' })),
        item.options && h('div', {
          key: 'opts',
          style: css({ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', paddingTop: '8px', marginTop: '4px', borderTop: `1px solid ${C.line}` })
        },
          h('span', { style: css({ fontSize: '11.5px', color: C.muted, marginRight: '4px' }) }, s.vhInterval),
          ...item.options.map(value => {
            const on = Math.abs(value - interval) < 1;
            return h('div', {
              key: 'i' + value, ...tap(() => actions.updateVehicle({ serviceInterval: value }), String(value)), role: 'radio', 'aria-checked': on ? 'true' : 'false',
              style: css({
                height: '36px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                border: `1px solid ${on ? C.accent : C.stroke}`, background: on ? C.line : 'transparent', fontFamily: MONO, fontSize: '12px', fontWeight: 600, color: on ? C.text : C.textDim
              })
            }, s.vhThousands(fmt.number(value / 1000, value % 1000 ? 1 : 0)));
          }))
      ))
  );
}

// ------------------------------------------------------------- service ---

export function serviceCard({ metrics, t, fmt, state, inputs, actions, compact = false, facts }) {
  const s = t.shell;
  const ui = state.vehUi;
  const f = facts || vehicleFacts(metrics, state);
  const rows = metrics.carRows || [];
  const total = rows.reduce((sum, row) => sum + (f.inBase(row.amount, row.currency) || 0), 0);
  const types = CAR_EXPENSE_TYPES.filter(type => rows.some(row => row.type === type));
  const filter = ui.filter !== 'all' && !types.includes(ui.filter) ? 'all' : ui.filter;
  const shown = rows.filter(row => filter === 'all' || row.type === filter);

  const months = [];
  for (const row of shown) {
    const key = String(row.date).slice(0, 7);
    let group = months.find(item => item.key === key);
    if (!group) months.push(group = { key, rows: [] });
    group.rows.push(row);
  }

  const startEdit = row => {
    actions.setVehUi({ svcOpen: true, svcEdit: row.id, svcType: row.type, svcDate: row.date, svcOil: Boolean(row.oilChange), rev: ui.rev + 1 });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('service-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };
  const chip = (key, text, count) => {
    const on = filter === key;
    return h('div', {
      key, ...tap(() => actions.setVehUi({ filter: key }), text), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        flex: '0 0 auto', height: '36px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 12px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${on ? C.strokeHi : C.stroke}`, background: on ? C.line : 'transparent', fontSize: '12.5px', color: on ? C.text : C.textDim
      })
    },
      h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: key === 'all' ? C.faint : colorOf(key) }) }),
      text, mono(String(count), { fontSize: '11px', color: C.muted }));
  };

  return box(compact, {},
    head(compact, s.vhService, rows.length ? s.vhServiceSub(rows.length, fmt.money(total)) : null),
    types.length > 0 && h('div', { key: 'filters', role: 'radiogroup', style: css({ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }) },
      chip('all', s.vhAll, rows.length),
      ...types.map(type => chip(type, carTypeLabel(t, type), rows.filter(row => row.type === type).length))),
    rows.length === 0
      ? h('div', { key: 'empty', style: css({ fontSize: '12.5px', color: C.muted }) }, s.vhServiceEmpty)
      : h('div', { key: 'months', style: css({ display: 'flex', flexDirection: 'column', gap: '14px' }) },
        ...months.map(group => h('div', { key: 'm' + group.key, style: css({ display: 'flex', flexDirection: 'column' }) },
          h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 2px 8px', borderBottom: `1px solid ${C.ghost}` }) },
            mono(fmt.monthName(group.key).toUpperCase(), { fontSize: '11px', letterSpacing: '1.2px', color: C.textDim }),
            mono(fmt.money(group.rows.reduce((sum, row) => sum + (f.inBase(row.amount, row.currency) || 0), 0)), { fontSize: '12.5px', fontWeight: 700 })),
          ...group.rows.map(row => h('div', {
            key: row.id,
            style: css({
              display: 'grid', gridTemplateColumns: compact ? '30px minmax(0,1fr) auto' : '44px minmax(0,1fr) auto auto', gap: '6px 12px', alignItems: 'center',
              minHeight: '56px', padding: '6px 2px', borderBottom: `1px solid ${C.line}`, background: ui.svcEdit === row.id ? C.line : 'transparent'
            })
          },
            mono(String(row.date).slice(8, 10), { fontSize: '12px', color: C.muted }),
            h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }) },
              h('span', { style: css({ fontSize: '13.5px', lineHeight: 1.35 }) }, row.note || carTypeLabel(t, row.type)),
              h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: colorOf(row.type) }) },
                h('span', { style: css({ width: '7px', height: '7px', borderRadius: '2px', background: colorOf(row.type) }) }), carTypeLabel(t, row.type),
                row.odo > 0 && h('span', { key: 'odo', style: css({ color: C.muted, fontFamily: MONO, fontSize: '11px' }) }, '· ' + fmt.distance(row.odo)),
                row.oilChange && h('span', {
                  key: 'oil', style: css({ padding: '1px 6px', borderRadius: '5px', background: GAIN_BG, fontFamily: MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.4px', color: C.accentHi })
                }, s.vhOilTag))),
            mono(fmt.amount(row.amount, row.currency), { fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }),
            h('div', { style: css({ display: 'flex', gap: '2px', ...(compact ? { gridColumn: '2 / -1', justifyContent: 'flex-end' } : {}) }) },
              iconButton('✎', () => startEdit(row), s.editCarExpenseTitle),
              iconButton('×', () => actions.removeCarExpense(row.id), s.removeLabel, true))
          ))
        ))),
    ui.svcOpen
      ? serviceForm({ t, fmt, state, inputs, actions, compact })
      : openButton(s.vhServiceNew, s.vhServiceNewHint, () => actions.setVehUi({ svcOpen: true, svcEdit: null, svcType: 'Maintenance', svcDate: today(), svcOil: false, rev: ui.rev + 1 }), 'open')
  );
}

function serviceForm({ t, fmt, state, inputs, actions, compact }) {
  const s = t.shell;
  const ui = state.vehUi;
  const row = ui.svcEdit ? (state.data?.carEx || []).find(item => item.id === ui.svcEdit) : null;
  const type = ui.svcType || 'Maintenance';
  const close = () => actions.setVehUi({ svcOpen: false, svcEdit: null, rev: ui.rev + 1 });
  const currency = row ? row.currency : actions.entryCurrency();
  const imperial = fmt.units.system === 'imperial';
  /* Shown in the reader's unit, stored in kilometres -- as on a fill-up. */
  const odoText = row && row.odo > 0 ? numberText(Math.round((imperial ? kmToMiles(row.odo) : row.odo) * 10) / 10) : undefined;

  const save = () => {
    const amount = parseNumber(inputs.value('vsAmt'));
    if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
    const typedOdo = inputs.value('vsOdo').trim();
    const reading = typedOdo === '' ? null : parseNumber(typedOdo);
    if (typedOdo !== '' && (reading === null || reading < 0)) return actions.showError(s.numberNeeded);
    /* Untouched, the stored kilometres stay exact rather than going through a rounded mile. */
    const odo = reading === null ? null : row && typedOdo === odoText ? row.odo : (imperial ? milesToKm(reading) : reading);
    const note = inputs.value('vsNote').trim();
    const cur = inputs.value('vsCur') || currency;
    const date = ui.svcDate || today();
    const oilChange = Boolean(ui.svcOil);
    if (row) actions.saveEdit('carEx', row.id, { type, note, amount, date, odo, oilChange });
    else actions.addCarExpense({ type, note, amount, currency: cur, date, odo, oilChange });
    inputs.clear(['vsNote', 'vsAmt', 'vsOdo']);
    close();
  };

  const chip = key => {
    const on = key === type;
    return h('div', {
      key, ...tap(() => actions.setVehUi({ svcType: key }), carTypeLabel(t, key)), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        height: '36px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 12px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${on ? colorOf(key) : C.stroke}`, background: on ? C.line : 'transparent', fontSize: '12.5px', color: on ? C.text : C.textDim
      })
    }, h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: colorOf(key) }) }), carTypeLabel(t, key));
  };

  return formShell({
    key: 'svcform-' + (row ? row.id : 'new') + '-' + ui.rev, id: 'service-form', editing: Boolean(row),
    title: row ? s.vhServiceEdit : s.vhServiceForm, onClose: close, closeLabel: s.cfClose
  },
    h('div', { role: 'radiogroup', style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px' }) }, ...CAR_EXPENSE_TYPES.map(chip)),
    fieldBox(inputs, 'vsNote', s.vhWhat, row ? row.note : undefined, { number: false, placeholder: s.vhWhatPh, onEnter: save }),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 130 : 140}px, 1fr))`, gap: '10px' }) },
      pickerField(s.dateLabel, datePicker({
        id: 'veh-svc-date', value: ui.svcDate || today(), onPick: stamp => stamp && actions.setVehUi({ svcDate: stamp }),
        label: s.dateLabel, title: s.serviceDateTitle, note: s.serviceDateNote, mobile: compact, state, actions, t, fmt, triggerStyle
      })),
      fieldBox(inputs, 'vsAmt', s.vhAmount, row ? numberText(row.amount) : undefined, { onEnter: save }),
      !row && currencyField(inputs, state, t, 'vsCur', currency),
      fieldBox(inputs, 'vsOdo', s.vhSvcOdoF, odoText, { unit: fmt.units.distance, placeholder: s.vhOptional, onEnter: save })),
    /*
     * Said, not guessed: the reminder counts from the entry marked here, so a
     * filter or a brake job with "olej" somewhere in its note no longer resets it.
     */
    checkBox(Boolean(ui.svcOil), s.vhOilMark, () => actions.setVehUi({ svcOil: !ui.svcOil }), 'oil'),
    h('div', { style: css({ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px' }) },
      h('div', { ...tap(close, s.cfCancel), style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 14px', color: C.muted, fontSize: '13px', cursor: 'pointer' }) }, s.cfCancel),
      pill(row ? s.cfSaveChanges : s.vhServiceAdd, save, { primary: true, key: 'save' }))
  );
}

/** The tab, for either layout. `vehicleForm` is the desktop's in-place editor for the car itself. */
export function vehicleTab(ctx, editor = null) {
  const facts = vehicleFacts(ctx.metrics, ctx.state);
  const all = { ...ctx, facts };
  const split = vehicleSplit(all);
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px' }) },
    vehicleStrip(all),
    editor,
    vehicleKpis(all),
    h('div', {
      key: 'main',
      style: css({ display: 'grid', gap: '14px', alignItems: 'start', gridTemplateColumns: ctx.compact ? 'minmax(0,1fr)' : 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))' })
    },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }) },
        fuelCard(all), split, vehicleUpcoming(all)),
      serviceCard(all))
  );
}
